import type { AgentConnectionState } from "@/hooks/useLiveKitAgent";
import {
  type VoiceSessionRecoveryKind,
  classifyVoiceSessionRecoveryError,
} from "@/lib/voice-session-recovery";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef } from "react";

export type VoiceDemoRecoverySurface = "landing_hero" | "onboarding_session" | "onboarding_quiz";

interface UseVoiceDemoRecoveryTelemetryOptions {
  audioRetryCount: number;
  audioTrackError?: string | null;
  connectionError?: string | null;
  connectionState: AgentConnectionState;
  enabled: boolean;
  isConnected: boolean;
  onClose?: () => void;
  recoveryKind: VoiceSessionRecoveryKind;
  retryAudioTrack?: () => void;
  retryConnection?: () => void;
  surface?: VoiceDemoRecoverySurface;
}

interface PendingConnectionRetry {
  errorCategory: string;
  startedAt: number;
}

export function useVoiceDemoRecoveryTelemetry({
  audioRetryCount,
  audioTrackError,
  connectionError,
  connectionState,
  enabled,
  isConnected,
  onClose,
  recoveryKind,
  retryAudioTrack,
  retryConnection,
  surface,
}: UseVoiceDemoRecoveryTelemetryOptions) {
  const posthog = usePostHog();
  const shownSignatureRef = useRef<string | null>(null);
  const pendingConnectionRetryRef = useRef<PendingConnectionRetry | null>(null);

  const capture = useCallback(
    (event: string, properties: Record<string, unknown>) => {
      if (!enabled || !surface) return;
      posthog.capture(event, { surface, ...properties });
    },
    [enabled, posthog, surface]
  );

  useEffect(() => {
    if (!enabled || !surface || !recoveryKind) {
      shownSignatureRef.current = null;
      return;
    }

    const errorCategory = classifyVoiceSessionRecoveryError(
      recoveryKind === "connection" ? connectionError : audioTrackError
    );
    const signature = `${recoveryKind}:${errorCategory}:${
      recoveryKind === "audio" ? audioRetryCount : 0
    }`;
    if (shownSignatureRef.current === signature) return;

    shownSignatureRef.current = signature;
    capture("voice_demo_recovery_shown", {
      connection_state: connectionState,
      error_category: errorCategory,
      failure_type: recoveryKind,
      retry_count: recoveryKind === "audio" ? audioRetryCount : 0,
    });
  }, [
    audioRetryCount,
    audioTrackError,
    capture,
    connectionError,
    connectionState,
    enabled,
    recoveryKind,
    surface,
  ]);

  useEffect(() => {
    const pendingRetry = pendingConnectionRetryRef.current;
    if (!pendingRetry) return;

    if (isConnected || connectionState === "connected" || connectionState === "ready") {
      capture("voice_demo_recovery_result", {
        duration_ms: Date.now() - pendingRetry.startedAt,
        error_category: pendingRetry.errorCategory,
        failure_type: "connection",
        outcome: "connected",
      });
      pendingConnectionRetryRef.current = null;
      return;
    }

    if (connectionState === "failed") {
      capture("voice_demo_recovery_result", {
        duration_ms: Date.now() - pendingRetry.startedAt,
        error_category: pendingRetry.errorCategory,
        failure_type: "connection",
        outcome: "failed",
      });
      pendingConnectionRetryRef.current = null;
    }
  }, [capture, connectionState, isConnected]);

  const trackedRetryConnection = useCallback(() => {
    if (!retryConnection) return;
    const errorCategory = classifyVoiceSessionRecoveryError(connectionError);
    pendingConnectionRetryRef.current = {
      errorCategory,
      startedAt: Date.now(),
    };
    capture("voice_demo_recovery_clicked", {
      connection_state: connectionState,
      error_category: errorCategory,
      failure_type: "connection",
      retry_count: 0,
    });
    retryConnection();
  }, [capture, connectionError, connectionState, retryConnection]);

  const trackedRetryAudio = useCallback(() => {
    if (!retryAudioTrack) return;
    const errorCategory = classifyVoiceSessionRecoveryError(audioTrackError);
    capture("voice_demo_recovery_clicked", {
      connection_state: connectionState,
      error_category: errorCategory,
      failure_type: "audio",
      retry_count: audioRetryCount,
    });
    retryAudioTrack();
    capture("voice_demo_recovery_result", {
      error_category: errorCategory,
      failure_type: "audio",
      outcome: "retry_requested",
      retry_count: audioRetryCount,
    });
  }, [audioRetryCount, audioTrackError, capture, connectionState, retryAudioTrack]);

  const trackedClose = useCallback(() => {
    const failureType = recoveryKind || (pendingConnectionRetryRef.current ? "connection" : null);
    if (failureType) {
      capture("voice_demo_exited_after_failure", {
        connection_state: connectionState,
        failure_type: failureType,
      });
    }
    onClose?.();
  }, [capture, connectionState, onClose, recoveryKind]);

  return {
    onClose: onClose ? trackedClose : undefined,
    retryAudioTrack: retryAudioTrack ? trackedRetryAudio : undefined,
    retryConnection: retryConnection ? trackedRetryConnection : undefined,
  };
}
